import React from 'react';

import { Box, BoxProps, Flex, LinkV2, SpacingStack, TextV2 } from '@withjoy/joykit';
import { useGetGuestsiteTravelListQuery, EventTravelFragment, TravelType, EventPageFragment } from '@graphql/generated';
import { ContentColumns } from '../../primitives';
import { ContentColumnProps } from '../../primitives/ContentColumn';
import { TravelBooking, travelTypeToComponent, travelTypeToIcon } from './TravelList.blocks';
import { TelemetryProvider } from './TravelList.telemetry';
import { useLayout } from '../../layouts/LayoutProvider';
import { isInIframe } from '@shared/utils/isInIframe';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { hed3ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { ACCOMMODATIONS_PAGE_SLUG } from '@apps/admin/routes/Planning/routes/Accommodations/Accommodations.constants';
import { useTranslation } from '@shared/core';
import { useFeatureValue } from '@shared/core/featureFlags';

export interface TravelListProps
  extends Readonly<{
      useMiniNav?: boolean;
      condensed?: boolean;
      eventId?: string;
      eventHandle: string;
      pages?: EventPageFragment[];
    }>,
    BoxProps {}

const _encodeNameToUrlHash = (name: string) => {
  return encodeURIComponent(name.trim().replace(/ /g, '-')).toLowerCase();
};

const TravelList = React.forwardRef<HTMLDivElement, TravelListProps>(({ eventId, useMiniNav = true, condensed, pages, ...restProps }, ref) => {
  const navItems: React.ReactElement[] = [];
  const containerColumnProps: ContentColumnProps = useMiniNav ? { span: [12, 'three-fifths'] } : { span: 12 };
  const { layout } = useLayout();
  const { eventHandle } = restProps;
  const isPreviewing = isInIframe();
  const { isAdmin } = useEventUserRole();
  const { t } = useTranslation('guestSiteTravel');
  const { linkText } = t('accommodationLink');
  const travelAccommodationConnectionEnabled = useFeatureValue('travelAccommodationConnectionEnabled')?.value === 'on';
  const { data, loading } = useGetGuestsiteTravelListQuery({
    variables: { eventHandle: eventHandle, isAdminDashboard: isPreviewing && isAdmin },
    batchMode: 'fast',
    fetchPolicy: 'cache-and-network',
    ssr: false,
  });

  if (loading) return <div style={{ height: '300px' }}></div>; // height set to prevent divs from jumping around while page loads

  const items = data?.eventByName?.info.travel || [];
  const accommodationItem = pages?.find(item => item?.pageSlug === ACCOMMODATIONS_PAGE_SLUG);
  const travelItems =
    accommodationItem?.visibility === 'public' && travelAccommodationConnectionEnabled
      ? [
          ...items,
          {
            id: accommodationItem?.id,
            name: accommodationItem?.pageTitle as EventTravelFragment['name'],
            type: TravelType.hotel,
            url: `/${eventHandle}/${ACCOMMODATIONS_PAGE_SLUG}`,
            urlTitle: linkText(),
            isBrannan: layout === 'brannan',
          },
        ]
      : items;

  const listMarkup = travelItems.map((item: { type: EventTravelFragment['type']; id?: EventTravelFragment['id']; name?: EventTravelFragment['name'] }) => {
    const { id, name, type } = item;
    const ItemIcon = travelTypeToIcon[type];
    {
      /* TODO: figure out how to type travelTypeToComponent for booking */
    }
    const ItemComponent = item.type === 'booking' ? TravelBooking : travelTypeToComponent[type as keyof typeof travelTypeToComponent];

    if (!ItemIcon || !ItemComponent) {
      return null;
    }

    const anchorId = _encodeNameToUrlHash(name || '');

    // To be added in later
    if (useMiniNav) {
      navItems.push(
        <li>
          <LinkV2
            wordBreak="break-word"
            href={`#${anchorId}`}
            textDecoration={'none'}
            typographyVariant={'button3'}
          >
            {name}
          </LinkV2>
        </li>,
      );
    }

    return (
      <ContentColumns
        key={id}
        data-testid={id}
        id={anchorId}
      >
        <ContentColumns.Column span={12}>
          {condensed ? (
            // aloha
            <Flex
              flexDirection={['column']}
              alignItems={'center'}
              justifyContent={['center']}
            >
              <ItemIcon size={40} />
              <GuestSiteTypographyOverride override={hed3ToSubHeadingOverride}>
                <TextV2
                  typographyVariant="hed3"
                  marginTop={[5]}
                  textAlign={['center']}
                  wordBreak="break-word"
                >
                  {name}
                </TextV2>
              </GuestSiteTypographyOverride>
            </Flex>
          ) : (
            // brannan
            <Flex
              flexDirection={['column', null, 'row']}
              alignItems={'center'}
              justifyContent={['center', 'initial']}
            >
              <ItemIcon
                size={40}
                marginLeft={[0, null, 6]}
              />
              <GuestSiteTypographyOverride override={hed3ToSubHeadingOverride}>
                <TextV2
                  typographyVariant="hed3"
                  marginLeft={[0, null, 5]}
                  marginTop={[5, null, 0]}
                  textAlign={['center', 'initial']}
                  wordBreak="break-word"
                >
                  {name}
                </TextV2>
              </GuestSiteTypographyOverride>
            </Flex>
          )}
        </ContentColumns.Column>

        <ContentColumns.Column
          span={condensed ? [12] : [12, 10]}
          offset={condensed ? [0] : [0, 2]}
          marginTop={6}
        >
          {/*Props of each ItemComponent are auto-generated and a bit different. id and name are required in some components */}
          {/*// @ts-ignore*/}
          <ItemComponent travelItem={item} />
        </ContentColumns.Column>
      </ContentColumns>
    );
  });

  return (
    <TelemetryProvider context={{ eventId: eventId, layout: layout }}>
      <Box
        ref={ref}
        {...restProps}
      >
        <ContentColumns>
          {useMiniNav && (
            <ContentColumns.Column span={'two-fifths'}>
              {/* <Box position="sticky" top={40} maxWidth={'75%'}>
              <SpacingStack as="ul" spacing={6}>
                {navItems}
              </SpacingStack>
            </Box> */}
            </ContentColumns.Column>
          )}
          <ContentColumns.Column {...containerColumnProps}>
            <SpacingStack spacing={10}>{listMarkup}</SpacingStack>
          </ContentColumns.Column>
        </ContentColumns>
      </Box>
    </TelemetryProvider>
  );
});

TravelList.displayName = 'TravelList';

export { TravelList };
